import { Clock3, Timer, CornerDownRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "../../lib/utils";
import { platformName, selectedPlatforms } from "../../lib/stream-platform";
import { PlatformIcon } from "../schedule/PlatformIcon";
import type { CalendarEvent } from "./schedule-events";
import { platformBadgeClasses, platformTones } from "./platform-tone";

export function SessionDetails({ event, title, duration }: { event: CalendarEvent; title: string; duration: string }) {
  const { t } = useTranslation();
  const tone = platformTones[event.platform];
  const platforms = selectedPlatforms(event.platform);
  const hasPlatforms = platforms.length > 0;
  const reference = event.reference;
  const barStyle = reference ? { backgroundColor: reference.color } : {};
  return (
    <div className="overflow-hidden rounded-md">
      <div className={cn("h-1", !reference && tone.bar)} style={barStyle} />
      <div className="space-y-3 p-4">
        {reference && <p className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: reference.color }}>{reference.name}</p>}
        {hasPlatforms && <div className="flex flex-wrap gap-1.5">
          {platforms.map(platform => (
            <span key={platform} className={cn("inline-flex items-center gap-1.5 rounded border px-2 py-1 text-[10px] font-semibold", platformBadgeClasses[platform])}>
              <PlatformIcon platform={platform} />{platformName(platform)}
            </span>
          ))}
        </div>}
        <h3 className="text-sm font-semibold leading-snug text-neutral-900 [overflow-wrap:anywhere]">{title}</h3>
        {event.subheading && <p className="text-xs leading-relaxed text-neutral-600 [overflow-wrap:anywhere]">{event.subheading}</p>}
        <dl className="space-y-2 border-t border-neutral-200 pt-3 text-xs">
          <div className="flex items-center justify-between gap-3">
            <dt className="flex items-center gap-1.5 text-neutral-500"><Clock3 aria-hidden="true" className="size-3.5" />{t("visualizer.timeRange")}</dt>
            <dd className="font-mono text-neutral-800">{event.time}–{event.endTime}{event.nextDay && <span className="ml-1 text-[10px] text-neutral-500">{t("visualizer.nextDay")}</span>}</dd>
          </div>
          <div className="flex items-center justify-between gap-3">
            <dt className="flex items-center gap-1.5 text-neutral-500"><Timer aria-hidden="true" className="size-3.5" />{t("schedule.duration")}</dt>
            <dd className="font-mono text-neutral-800">{duration}</dd>
          </div>
        </dl>
        {event.continued && <p className="flex items-center gap-1.5 rounded bg-neutral-100 px-2 py-1.5 text-[10px] text-neutral-600"><CornerDownRight aria-hidden="true" className="size-3.5" />{t("visualizer.continued")}</p>}
      </div>
    </div>
  );
}
